import React, { useState, useContext, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import Skeleton from 'react-loading-skeleton';
import classnames from 'classnames';
import * as RadioGroup from '@radix-ui/react-radio-group';
import * as Label from '@radix-ui/react-label';
import { animated, useTransition } from 'react-spring';

import { EditableFieldsContext } from '../context';

import { FieldDisplay, EditableField, FieldProps } from './utils';

import { FormikTextField } from 'components/fields/FormikTextField';
import { Btn } from 'components/btn/Btn';
import {
    addressLine2Validator,
    addressLine1Validator,
    cityValidator,
    stateValidator,
    zipCodeValidator,
} from 'common/modules/form-validation/validators';
import { useRootSelector } from 'common/features/featuresReducer';
import { RootState } from 'rootStore';
import { useAddresses } from 'common/hooks/useAddresses';
import { FamilyAddress } from 'common/api/crm/family/address/models/FamilyAddress';
import { createStyles } from 'utils/createStyle';
import { useDefaultAddress } from 'common/hooks/useDefaultAddress';
import { FormikSelectField } from 'components/fields/FormikSelectField';
import { stateOptions } from 'common/modules/form-validation/utils/stateOptions';
import { addAddress, deleteAddress, editAddress } from 'common/api/crm/family/address';
import { LoadingOverlay, openLoader, closeLoader } from 'components/loading-overlay/LoadingOverlay';
import { useAlert } from 'modules/alerts';
import { SERVICE_UNAVAILABLE_MESSAGE } from 'config';
import { Modal, ModalFooter, ModalSecondaryBtn } from 'components/modal/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { colors } from 'styles/settings';

const styles = createStyles({
    defaultBox: [
        'border border-1 text-center px-3 cursor-pointer rounded rounded-1 ml-2 sm:mt-0 mt-2',
        {
            lineHeight: '15px',
            height: 15,
            fontSize: 10,
        },
    ],
    defaultBoxActive: ['border-brand bg-brand text-white'],
    address: ['', { fontSize: 17 }],
    styledIndicator: [
        'bg-black text-brand block',
        {
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: 'dodgerblue',
        },
    ],
    styledRadio: [
        'align-middle mr-2 cursor-pointer',
        {
            appearance: 'none',
            backgroundColor: 'transparent',
            border: 'none',
            padding: 0,
            borderRadius: '50%',
            boxShadow: 'inset 0 0 0 1px gainsboro',
            minWidth: 15,
            width: 15,
            height: 15,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',

            '& ~ &': { marginLeft: 5 },

            '&:focus': {
                outline: 'none',
                boxShadow: 'inset 0 0 0 1px dodgerblue, 0 0 0 1px dodgerblue',
            },
        },
    ],
});

interface AddressEditableProps extends FieldProps {
    loading: boolean;
    addresses?: FamilyAddress[];
    addressBeingEdited?: string;
    setAddressBeingEdited: (_?: string) => void;
    createMode: boolean;
    setCreateMode: (_: boolean) => void;
    refreshAddresses: Function;
}

interface AddressProps {
    address: FamilyAddress;
    isDefaultAddress: boolean | null;
    fieldBeingEdited: boolean;
    createModeOn: boolean;
    setDefaultAddress: (_: FamilyAddress) => Promise<boolean>;
    isAnyFieldBeingEdited: boolean;
    refreshAddresses: Function;
}

const Address = ({
    address,
    isDefaultAddress,
    fieldBeingEdited,
    createModeOn,
    setDefaultAddress,
    refreshAddresses,
}: AddressProps) => {
    const fullAddress = `${address.address1} ${address.address2}, ${address.city}, ${address.state} ${address.zipCode}`;
    const alert = useAlert();

    const setAddressAsDefault = async () => {
        // setDefaultAddress returns `true` on success and `false` for unsunccessful calls
        const successfulUpdate = await setDefaultAddress(address);

        if (!successfulUpdate) {
            alert({ message: SERVICE_UNAVAILABLE_MESSAGE });
            return;
        }

        refreshAddresses();
    };

    const AddressInfo = () => (
        <div className="flex sm:items-center items-start w-full sm:w-auto justify-between">
            <p className={styles.address}>{fullAddress}</p>
            <p
                className={classnames(
                    styles.defaultBox,
                    isDefaultAddress ? styles.defaultBoxActive : null
                )}
                onClick={setAddressAsDefault}
            >
                Default
            </p>
        </div>
    );

    return (
        <div className="flex items-center mb-3 ">
            {fieldBeingEdited && !createModeOn && (
                <>
                    <RadioGroup.Item
                        id={address.id}
                        value={address.id}
                        className={styles.styledRadio}
                        arial-label={`Selecting: ${fullAddress}`}
                    >
                        <RadioGroup.Indicator className={styles.styledIndicator} />
                    </RadioGroup.Item>
                    <Label.Root className="sm:w-auto w-full">
                        <AddressInfo />
                    </Label.Root>
                </>
            )}
            {(!fieldBeingEdited || createModeOn) && <AddressInfo />}
        </div>
    );
};

interface AddressListProps
    extends Pick<AddressEditableProps, 'id' | 'addressBeingEdited' | 'setAddressBeingEdited'> {
    addresses: FamilyAddress[];
    createModeOn: boolean;
    refreshAddresses: Function;
}

export const AddressList = ({
    id,
    addresses,
    setAddressBeingEdited,
    addressBeingEdited,
    createModeOn,
    refreshAddresses,
}: AddressListProps) => {
    const { isDefaultAddress, refreshDefaultAddress, setDefaultAddress } = useDefaultAddress();
    const { fieldToEdit } = useContext(EditableFieldsContext);

    useEffect(() => {
        refreshDefaultAddress();
    }, [refreshDefaultAddress]);

    if (addresses.length === 0) {
        return <div>No Addresses found.</div>;
    }

    const onValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const selectedAddress = addresses.find((address: FamilyAddress) => address.id === value);
        setAddressBeingEdited(selectedAddress?.id ?? undefined);
    };

    return (
        <div>
            <RadioGroup.Root
                tabIndex={undefined}
                value={addressBeingEdited}
                onValueChange={onValueChange}
            >
                {addresses.map((address: FamilyAddress) => (
                    <Address
                        key={address.id}
                        address={address}
                        isDefaultAddress={isDefaultAddress(address)}
                        fieldBeingEdited={fieldToEdit === id}
                        isAnyFieldBeingEdited={Boolean(fieldToEdit)}
                        createModeOn={createModeOn}
                        setDefaultAddress={setDefaultAddress}
                        refreshAddresses={refreshAddresses}
                    />
                ))}
            </RadioGroup.Root>
        </div>
    );
};

export const AddressFieldDisplay = (props: AddressEditableProps) => {
    const { setFieldToEdit } = useContext(EditableFieldsContext);
    if (props.loading) {
        return <Skeleton className="h-10" />;
    }

    const addNewAddress = () => {
        setFieldToEdit(props.id);
        props.setCreateMode(true);
    };

    const onToggle = () => {
        props.setAddressBeingEdited('');
        props.setCreateMode(false);
    };

    return (
        <FieldDisplay label="Address" onToggle={onToggle} {...props}>
            <AddressList
                id={props.id}
                addresses={props.addresses || []}
                addressBeingEdited={props.addressBeingEdited}
                setAddressBeingEdited={props.setAddressBeingEdited}
                createModeOn={props.createMode}
                refreshAddresses={props.refreshAddresses}
            />
            {!props.isEditing && (
                <p onClick={addNewAddress} className="underline text-brand text-xs cursor-pointer">
                    Add Address
                </p>
            )}
        </FieldDisplay>
    );
};

interface AddressFields {
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
}

export const AddressFieldEditComponent = (props: AddressEditableProps) => {
    const address =
        (props.addresses as FamilyAddress[]).find(
            (address_: FamilyAddress) => address_.id === props.addressBeingEdited
        ) || null;
    const profile = useRootSelector((state: RootState) => state.store.home.homeDetails?.profile);
    const { setFieldToEdit } = useContext(EditableFieldsContext);
    const [errorExists, setErrorExists] = useState(false);
    const ref = useRef<LoadingOverlay>(null);
    const { isDefaultAddress, refreshDefaultAddress } = useDefaultAddress();
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

    useEffect(() => {
        refreshDefaultAddress();
    }, [refreshDefaultAddress, props.addresses]);

    const showForm = props.createMode || (props.isEditing && props.addressBeingEdited);
    const transitions = useTransition(showForm, {
        from: { opacity: 0, transform: 'translate2d(0%, 0%)', maxHeight: '0px' },
        enter: { opacity: 1, transform: 'translate2d(0%, 100%)', maxHeight: '1000px' },
        leave: { opacity: 0, transform: 'translate2d(0%, 0%)', maxHeight: '0px' },
    });

    const initialValues = {
        id: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        ...address,
    };

    const onDeleteClicked = () => {
        setShowDeleteModal(true);
    };

    const onDeleteDismiss = () => {
        setShowDeleteModal(false);
    };

    const doDeleteAddress = async () => {
        if (!address) {
            return;
        }
        try {
            openLoader(ref.current);
            await deleteAddress(address.id);
            closeLoader(ref.current);
            props.refreshAddresses();
            onDeleteDismiss();
            setFieldToEdit('');
        } catch (e) {
            setErrorExists(true);
        }
    };

    const onSubmit = async (values: AddressFields) => {
        const payload = {
            ...values,
            id: initialValues.id || '',
            firstName: profile?.firstName,
            lastName: profile?.lastName,
            phoneNumber: '', // we don't have access of the phoneNumber, where should we get it from?
        };

        try {
            openLoader(ref.current);
            await (props.createMode
                ? addAddress(payload as FamilyAddress)
                : editAddress(payload as FamilyAddress));
            closeLoader(ref.current);
            props.refreshAddresses();
            props.setCreateMode(false);
            setFieldToEdit('');
            props.setAddressBeingEdited(undefined);
        } catch (e) {
            setErrorExists(true);
        }
    };

    const getAddressToShowOnDelete = () => {
        if (!address) {
            return '';
        }
        const { address1, address2, city, state, zipCode } = address;
        return `${address1}${address2 ? `, ${address2}, ` : ', '}${city}, ${state} ${zipCode}`;
    };

    return transitions(
        (styles_, item) =>
            item && (
                <animated.div style={styles_} className="mt-5">
                    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSubmit}>
                        <Form>
                            <div className="flex flex-col mb-5">
                                <div className="mb-5 flex flex-row">
                                    <div className="mr-2 flex-1">
                                        <FormikTextField
                                            name="address1"
                                            label="Address"
                                            validate={addressLine1Validator}
                                        />
                                    </div>
                                    <div className="w-16">
                                        <FormikTextField
                                            name="address2"
                                            label="Ste/Apt/Attn"
                                            validate={addressLine2Validator}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-row mb-5">
                                    <div className="flex-1 mr-2">
                                        <FormikTextField
                                            name="city"
                                            label="City"
                                            validate={cityValidator}
                                        />
                                    </div>
                                    <div className="w-16">
                                        <FormikSelectField
                                            label="State"
                                            name="state"
                                            validate={stateValidator}
                                        >
                                            <option value="" />
                                            {stateOptions.map((o) => (
                                                <option key={o.value}>{o.value}</option>
                                            ))}
                                        </FormikSelectField>
                                    </div>
                                </div>
                                <div>
                                    <FormikTextField
                                        label="Zip"
                                        name="zipCode"
                                        validate={zipCodeValidator}
                                    />
                                </div>
                            </div>
                            {errorExists && (
                                <p className="text-sm mb-3 text-error">
                                    Unable to update your information at this time
                                </p>
                            )}
                            <div className="flex">
                                {address ? (
                                    <Btn
                                        className="mr-2"
                                        isSecondary
                                        disabled={!!address && !!isDefaultAddress(address)}
                                        type="button"
                                        onClick={onDeleteClicked}
                                    >
                                        Delete
                                    </Btn>
                                ) : (
                                    <Btn
                                        isSecondary
                                        type="button"
                                        className="mr-2"
                                        onClick={() => setFieldToEdit('')}
                                    >
                                        Cancel
                                    </Btn>
                                )}
                                <Btn type="submit">Save</Btn>
                            </div>
                        </Form>
                    </Formik>
                    <Modal title="" isOpen={showDeleteModal} onClose={onDeleteDismiss}>
                        <div className="flex flex-col items-center">
                            <FontAwesomeIcon
                                color={colors.error}
                                icon={faExclamationTriangle}
                                size="2x"
                            />
                            <div className="mt-6 text-center w-auto sm:w-9/12 ">
                                <p className="text-2xl font-semibold mb-4">
                                    Are you sure you want to delete this address?
                                </p>
                                <p className="text-grey-2 font-regular text-sm">
                                    {getAddressToShowOnDelete()}
                                </p>
                            </div>
                        </div>
                        <ModalFooter>
                            <Btn type="button" onClick={doDeleteAddress} className="px-2">
                                Yes
                            </Btn>
                            <ModalSecondaryBtn isSecondary onClick={onDeleteDismiss}>
                                Cancel
                            </ModalSecondaryBtn>
                        </ModalFooter>
                    </Modal>
                </animated.div>
            )
    );
};

// NOTE: probably better to lift the state into a context to share state much better.
// For now it'll do.
export const AddressEditableField = ({ fieldToEdit }: { fieldToEdit: string }) => {
    const [addressBeingEdited, setAddressBeingEdited] = useState<string | undefined>();
    const [createMode, setCreateMode] = useState<boolean>(false);
    const { addresses, refreshAddresses } = useAddresses();
    const [loading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        refreshAddresses().finally(() => {
            setIsLoading(false);
        });
    }, [refreshAddresses, createMode]);

    return (
        <EditableField<AddressEditableProps>
            id="addressField"
            className="mb-8"
            editMode={Boolean(fieldToEdit)}
            isEditing={fieldToEdit === 'addressField'}
            FieldDisplayComponent={AddressFieldDisplay}
            FieldEditComponent={AddressFieldEditComponent}
            addressBeingEdited={addressBeingEdited}
            setAddressBeingEdited={setAddressBeingEdited}
            addresses={addresses || []}
            loading={loading}
            createMode={createMode}
            setCreateMode={setCreateMode}
            refreshAddresses={refreshAddresses}
        />
    );
};
